import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { usePatient } from "../context/PatientContext";
import AddNewVisit from "./Add/AddNewVisit";
import CONFIG from "../config";
import "../Styles/patientDetails.css";

const PatientVisits = () => {
  const { patientId, caseId } = useParams();
  const { verifyTokenAndRedirect } = useAuth();
  const { patientData, setPatientData, fetchVisitsForCase, setSelectedVisit, selectedVisit, doctorInfo } = usePatient();

  const [visits, setVisits] = useState([]);
  const [patientName, setPatientName] = useState("");
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const baseURL = CONFIG.BASE_URL;
  const location = useLocation();
  const token = localStorage.getItem("authToken");

  /**
   * 🔹 Ensure Authentication
   */
  useEffect(() => {
    verifyTokenAndRedirect();
  }, []);

  /**
   * 🔹 Fetch Balance
   */
  const fetchBalance = async () => {
    try {
      const response = await axios.get(`${baseURL}/patients/${patientId}/balance`, {
        headers: {
          Authorization: `Bearer ${token}`,
          doctorId: doctorInfo?.MANDALI_ID,
          clinicId: doctorInfo?.CLINIC_ID,
          roleId: doctorInfo?.MANDALI_ROLE,
        },
      });
      setBalance(response.data.previous_balance || 0);
    } catch (error) {
      console.error("Error fetching patient balance:", error);
      setBalance(0);
    }
  };

  /**
   * 🔹 Fetch Visits for Selected Case
   */
  useEffect(() => {
    const loadVisits = async () => {
      try {
        const data = await fetchVisitsForCase(patientId, caseId);
        setVisits(data || []);
        if (data.length > 0) {
          setPatientName(data[0].PATIENT_NAME || "Unknown Patient");
        }
      } catch (error) {
        console.error("Error fetching visits:", error);
      } finally {
        setLoading(false);
      }
    };

    loadVisits();
    fetchBalance(); // Fetch balance initially
  }, [location.key, fetchVisitsForCase, patientId, caseId]);

  /**
   * 🔹 Handle Selecting a Visit
   */
  const handleVisitSelect = useCallback(
    (visitId) => {
      const updatedPatientData = { ...patientData, ACTION_STATUS: "UPDATE_EXISTING_VISIT" };
      setPatientData(updatedPatientData);
      setSelectedVisit(visitId);
      navigate(`/visits/${patientId}/${caseId}/${visitId}`);
    },
    [navigate, patientId, caseId, patientData]
  );

  if (loading) return <h3>Loading patient visits...</h3>;

  return (
    <div className="patient-container">
      {/* 🧾 Header Section */}
      <div className="patient-header">
        <div className="patient-info">
          <h2>{patientName}</h2>
          <p>Patient ID: {patientId}</p>
        </div>

        {/* 💰 Balance Card */}
        <div className="balance-card">
          <div className="balance-top">
            <h4>Account Balance</h4>
          </div>
          <p className={balance >= 0 ? "positive" : "negative"}>
            ₹ {balance?.toLocaleString("en-IN")}
          </p>
        </div>
      </div>

      {/* 🧩 Visits Section */}
      <div className="container1">
        <div className="menu">
          <AddNewVisit
            patientVisits={visits}
            patientId={patientId}
            caseId={caseId}
            actionstatus="ADD_NEW_VISIT"
          />
        </div>

        <div className="info">
          <h3>Patient Visits</h3>
          {visits.length > 0 ? (
            <ul className="case-list">
              {visits.map((visit) => (
                <li key={visit.VISIT_ID} onClick={() => handleVisitSelect(visit.VISIT_ID)}>
                  <strong>Visit ID:</strong> {visit.VISIT_ID} |
                  <strong> Symptoms:</strong> {visit.PATIENT_SYMPTOMS} |
                  <strong> Treatment:</strong> {visit.TREATMENT_DETAILS}
                </li>
              ))}
            </ul>
          ) : (
            <p>No visits found for this case.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientVisits;
